import { useGlobalSearchStore } from "./globalSearchStore";
import {
	AlertManager,
	EffectiveTheme,
	MediaQueryKind,
	useAlerts,
	useEffectiveTheme,
	useUnmountedCancellation,
	useUserTimezone,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	ReactElement,
	RefObject,
	useCallback,
	useMemo,
	useRef
} from "react";
import { SearchIntegrationKind, validateAuthentications } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { shallowComparison } from "client/Misc/compareUtils";
import { WorkspaceHeaderStyle } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { SearchAndResults, SearchAndResultsHandle } from "./SearchAndResults/SearchAndResults";
import { SearchForm, SearchFormHandle } from "./SearchForm/SearchForm";
import {
	createUrlRelativeToEnvironment,
	navigateToUriInPopOutWindow
} from "client_legacy/ndaw/navigator";
import { useFetchGlobalSearchResults } from "./useFetchGlobalSearchResults";
import { Theme } from "client/Api/Branding/UserTheme/userTheme";
import { TimeZone } from "client/DateAndTime/timezones";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { useSearchNodes } from "./useSearchNodes";
import { useFrequentlyUsedTileNavigation } from "../../FrequentlyUsedTiles/useFrequentlyUsedTileNavigation";
import { useContactDialog } from "client/Features/AddressBook/ContactDialog";
import { ContactSource } from "client/Api/Email/contacts";
import { AuthenticateIntegrationFlowResultKind, useTryAuthenticateIntegrationFlow } from "./useAuthenticateIntegrationFlow";

export type ScopeActions = {
	onAuthenticate?: () => void;
	onIntegrationFlowAuthenticate?: (integrationId: number, integrationName: string) => void;
};

export type GlobalSearchProps = {
	headerStyle: WorkspaceHeaderStyle;
};

export const GlobalSearch = (props: GlobalSearchProps): ReactElement | null => {
	const {
		headerStyle
	} = props;

	const alertManager: AlertManager = useAlerts();

	const {
		nodes,
		allAvailableScopes
	} = useSearchNodes();

	useFetchGlobalSearchResults(allAvailableScopes);

	const [authenticationDialogs, authenticate] = useTryAuthenticateIntegrationFlow();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const userTimezone: TimeZone = useUserTimezone();
	const effectiveTheme: EffectiveTheme = useEffectiveTheme();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const searchAndResultRef: RefObject<SearchAndResultsHandle | null> = useRef<SearchAndResultsHandle>(null);

	const effectiveHeaderStyle: WorkspaceHeaderStyle = effectiveTheme === Theme.Dark
		? WorkspaceHeaderStyle.WhiteBackground
		: headerStyle;

	const {
		setSearchTerm,
		triggerSearch
	} = useGlobalSearchStore(
		(x) => ({
			setSearchTerm: x.setSearchTerm,
			triggerSearch: x.triggerSearch
		}),
		shallowComparison
	);

	const triggerSearchHandler = useCallback(
		(): void => {
			triggerSearch();
			searchAndResultRef.current
				?.open();
		},
		[triggerSearch]
	);

	const searchHandler = (searchTerm: string): void => {
		setSearchTerm(searchTerm);
		triggerSearchHandler();
	};

	const [tileNavigationElement, invokeFrequentlyUsedTile] = useFrequentlyUsedTileNavigation(userTimezone);
	const [contactDetailsDialog, openContactDetailsDialog] = useContactDialog();

	const availableActionsMap = useMemo(
		(): ReadonlyMap<AvailableActionsIdentifier, ScopeActions> => {
			const options: Map<AvailableActionsIdentifier, ScopeActions> = new Map<SearchIntegrationKind, ScopeActions>();

			options.set(
				SearchIntegrationKind.Egnyte,
				{
					onAuthenticate: () => {
						navigateToUriInPopOutWindow(
							createUrlRelativeToEnvironment(
								"TitleBar/Search/Egnyte/Authenticate"
							)
						);
					}
				}
			);

			options.set(
				"integration-flow",
				{
					onIntegrationFlowAuthenticate: async (
						integrationId: number,
						integrationName: string
					): Promise<void> => {
						try {
							const {
								authentications: missingAuthentications
							} = await validateAuthentications(integrationId, globalCancellation);

							if (missingAuthentications.length === 0) {
								triggerSearchHandler();
								return;
							}

							const result: TryResult<AuthenticateIntegrationFlowResultKind> = await authenticate(
								integrationName,
								missingAuthentications
							);

							if (result.wasSuccessful && result.result === "completed") {
								triggerSearchHandler();
							}
						} catch(e) {
							if (isCancelledError(e)) {
								return;
							}

							console.error(e);
							alertManager.showError(localize("_GeneralErrorMessage"));
						}
					}
				}
			);

			return options;
		},
		[
			alertManager,
			globalCancellation,
			triggerSearchHandler,
			authenticate
		]
	);

	const openContactDetailsHandler = useCallback(
		(id: string, source: ContactSource): void => {
			if (source === ContactSource.Suggested) {
				throw new Error("Address book does not support suggested contacts");
			}

			openContactDetailsDialog({
				source: source,
				initialView: {
					kind: "details",
					contactId: id
				}
			});
		},
		[openContactDetailsDialog]
	);

	if (isResponsive) {
		return null;
	}

	return (
		<>
			<SearchAndResults
				nodes={nodes}
				headerStyle={headerStyle}
				onSearch={searchHandler}
				availableShowMoreOptions={availableActionsMap}
				renderSearchForm={(options) => renderSearchForm({
					...options,
					effectiveHeaderStyle
				})}
				timezone={userTimezone}
				onInvokeFrequentlyUsedTile={invokeFrequentlyUsedTile}
				onOpenContactDetails={openContactDetailsHandler}
				ref={searchAndResultRef}
			/>

			{tileNavigationElement}
			{authenticationDialogs}
			{contactDetailsDialog}
		</>
	);
};

GlobalSearch.displayName = "GlobalSearch";

export type AvailableActionsIdentifier =
	| SearchIntegrationKind
	| "integration-flow";

export type SearchFormOptions = {
	effectiveHeaderStyle: WorkspaceHeaderStyle;
	onSearch(searchTerm: string): void;
	lastUsedSearchTerm: string;
	inputRef: RefObject<SearchFormHandle | null>;
	onNavigateToResults: () => void;
	onFocusInput: () => void;
	onCloseResults: () => void;
};

function renderSearchForm(
	options: SearchFormOptions
): ReactElement {
	const {
		effectiveHeaderStyle,
		onSearch,
		lastUsedSearchTerm,
		inputRef,
		onNavigateToResults,
		onFocusInput,
		onCloseResults
	} = options;

	return (
		<SearchForm
			onSearch={onSearch}
			lastUsedSearchTerm={lastUsedSearchTerm}
			ref={inputRef}
			onNavigateToResults={onNavigateToResults}
			onFocusInput={onFocusInput}
			onCloseResults={onCloseResults}
			effectiveHeaderStyle={effectiveHeaderStyle}
		/>
	);
}